import React, {
  createContext,
  useReducer,
  useEffect,
  useContext,
  useState,
  useRef,
} from 'react'
import { Storage } from '../../utils.jsx'
import { GameModeContext, useGameMode } from '../GameModes/GameModes.jsx'
import { GameStatsContext } from '../GameStats/GameStats.jsx'
import * as allWords4 from '../../data/static/allWords4.js'
import * as allWords5 from '../../data/static/allWords5.js'
import { customSnackbar } from '../../Hooks/CustomSnackbar.js'
import { submitPuzzleCompletion } from '../../utils/communityStats.js'

const ALL_WORDS = {
  4: allWords4.VALID_WORDS,
  5: allWords5.VALID_WORDS,
}

const DIFFICULTY_LABELS = [
  'Easy',
  'Moderate',
  'Challenging',
  'Hard',
  'Brutal',
]

export function normalizeTodaysWords(words) {
  return {
    puzzle_number: words.puzzle_number,
    4: words.standard4,
    5: words.standard5,
  }
}

export async function loadTodaysWords(fetchImpl = fetch, date = new Date()) {
  const timestamp = new Date(date).setHours(0, 0, 0, 0)
  const response = await fetchImpl(
    `/data/dynamic/todays_puzzle.json?t=${timestamp}`,
  )
  if (!response.ok) {
    throw new Error(`Unable to load today's puzzle (${response.status})`)
  }
  return normalizeTodaysWords(await response.json())
}

export function getDifficulty(words) {
  const fallbackRank = Math.max(1, Math.min(5, words.best_score - 2))
  const rank = words.difficulty_rank ?? fallbackRank
  return {
    rank,
    label: words.difficulty_label ?? DIFFICULTY_LABELS[rank - 1],
  }
}

export const GameStateContext = createContext()
export const LinkRowRefContext = createContext()

export function centerLinkDiv(linkRowRef) {
  if (linkRowRef.current) {
    const rect = linkRowRef.current.getBoundingClientRect()
    const centerY = (window.innerHeight - 230) / 2
    const toScroll = rect.top + window.scrollY - (centerY - rect.height / 2)
    window.scrollTo({ top: toScroll, behavior: 'smooth' })
  }
}

export function getMatchingLetters(upperWord, lowerWord) {
  const matchingIndices = []
  for (let i = 0; i < upperWord.length; i++) {
    if (upperWord[i] === lowerWord[i]) {
      matchingIndices.push(i)
    }
  }
  return matchingIndices
}

export function gameReducer(state, action) {
  switch (action.type) {
    case 'INITIALIZE_NEW_GAME':
      switch (action.payload.gameMode) {
        case 'standard':
          const newWords = action.payload.todaysWords[action.payload.wordLength]
          const newValidWords = ALL_WORDS[action.payload.wordLength]
          const difficulty = getDifficulty(newWords)
          return {
            ...state,
            wordLength: action.payload.wordLength,
            validWords: newValidWords,
            todaysWords: newWords,
            startWord: newWords.start_word,
            endWord: newWords.end_word,
            bestScore: newWords.best_score,
            avgScore: newWords.average_score,
            difficultyRank: difficulty.rank,
            difficultyLabel: difficulty.label,
            communityStats: null,
            isCommunityStatsLoading: false,
            puzzle_number: action.payload.todaysWords.puzzle_number,
            upper: [
              [
                newWords.start_word,
                Array.from({ length: action.payload.wordLength }, (_, i) => i),
              ],
            ],
            link: '',
            lower: [
              [
                newWords.end_word,
                Array.from({ length: action.payload.wordLength }, (_, i) => i),
              ],
            ],
            won: false,
            wasStarted: false,
            hasSeenRules: action.payload.hasSeenRules,
          }
      }

    case 'INIT_STATE_COMPLETED':
      return {
        ...state,
        isInitialized: true,
      }

    case 'LOAD_SAVED_STATE':
      console.debug('Loaded saved game state:', action.payload.savedGameState)
      return { ...state, ...action.payload.savedGameState }

    case 'HANDLE_STALE_GAME_DATA':
      console.debug('Handling stale game data:', action.payload)
      const { currentGame, statsState } = action.payload

      if (currentGame && !currentGame.won && currentGame.wasStarted) {
        return { ...state, handleLoss: true }
      } else if (
        state.puzzle_number - currentGame.puzzle_number > 1 &&
        statsState.streak >= 1
      ) {
        return { ...state, handleStreakDown: true }
      }
      return { ...state }

    case 'RESET_HANDLE_LOSS':
      return { ...state, handleLoss: false }

    case 'RESET_HANDLE_STREAK_DOWN':
      return { ...state, handleStreakDown: false }

    case 'SET_GAME_STARTED':
      return { ...state, wasStarted: true }

    case 'SET_HAS_SEEN_RULES':
      return {
        ...state,
        hasSeenRules: action.payload,
      }

    case 'SET_LINK':
      return { ...state, link: action.payload, won: true }
    case 'COMMUNITY_STATS_LOADING':
      return { ...state, isCommunityStatsLoading: true }
    case 'SET_COMMUNITY_STATS':
      return {
        ...state,
        communityStats: action.payload,
        isCommunityStatsLoading: false,
      }
    case 'COMMUNITY_STATS_UNAVAILABLE':
      return { ...state, isCommunityStatsLoading: false }
    case 'ADD_TO_UPPER':
      centerLinkDiv(action.payload.linkRowRef)
      return {
        ...state,
        wasStarted: true,
        upper: [
          ...state.upper,
          [action.payload.word, action.payload.indexOfChange],
        ],
      }
    case 'ADD_TO_LOWER':
      centerLinkDiv(action.payload.linkRowRef)
      return {
        ...state,
        wasStarted: true,
        lower: [
          ...state.lower,
          [action.payload.word, action.payload.indexOfChange],
        ],
      }
    default:
      return state
  }
}

export function GameStateProvider({ children, puzzleLoader = loadTodaysWords }) {
  const [todaysWords, setTodaysWords] = useState(null)

  useEffect(() => {
    let cancelled = false
    puzzleLoader()
      .then((words) => {
        if (!cancelled) setTodaysWords(words)
      })
      .catch((error) => console.error("Error loading today's puzzle:", error))
    return () => {
      cancelled = true
    }
  }, [puzzleLoader])

  if (!todaysWords) return null

  return (
    <LoadedGameStateProvider todaysWords={todaysWords}>
      {children}
    </LoadedGameStateProvider>
  )
}

function LoadedGameStateProvider({ children, todaysWords: todaysWordsByLength }) {
  const { showWarningSnackbar } = customSnackbar()
  const linkRowRef = useRef(null)
  const { gameMode } = useGameMode()
  const wordLength = parseInt(gameMode.slice(-1))
  const validWords = ALL_WORDS[wordLength]
  const todaysWords = todaysWordsByLength[wordLength]
  const difficulty = getDifficulty(todaysWords)
  const initialState = {
    isInitialized: false,
    gameMode: gameMode,
    wordLength: wordLength,
    validWords: validWords,
    todaysWords: todaysWords,
    startWord: todaysWords.start_word,
    endWord: todaysWords.end_word,
    bestScore: todaysWords.best_score,
    avgScore: todaysWords.average_score,
    difficultyRank: difficulty.rank,
    difficultyLabel: difficulty.label,
    communityStats: null,
    isCommunityStatsLoading: false,
    puzzle_number: todaysWordsByLength.puzzle_number,
    upper: [
      [todaysWords.start_word, Array.from({ length: wordLength }, (_, i) => i)],
    ],
    link: '',
    lower: [
      [todaysWords.end_word, Array.from({ length: wordLength }, (_, i) => i)],
    ],
  }
  const [state, dispatch] = useReducer(gameReducer, initialState)
  const { state: statsState, dispatch: statsDispatch } =
    useContext(GameStatsContext)

  const [handleLoss, setHandleLoss] = useState(false)
  const [handleStreakDown, setHandleStreakDown] = useState(false)
  const [lastKeyPressed, setLastKeyPressed] = useState(null)
  const [goldenIndices, setGoldenIndices] = useState([])
  const [lastAdded, setLastAdded] = useState(null)

  useEffect(() => {
    document.title = `Chaindle ${state.wordLength} - #${state.puzzle_number}`
  }, [state.wordLength, state.puzzle_number])

  useEffect(() => {
    if (!state.isInitialized || !state.won) {
      return undefined
    }

    let cancelled = false
    const score = state.upper.length + state.lower.length - 1
    dispatch({ type: 'COMMUNITY_STATS_LOADING' })

    submitPuzzleCompletion({
      puzzleNumber: state.puzzle_number,
      gameMode,
      score,
    })
      .then((communityStats) => {
        if (!cancelled) {
          dispatch({ type: 'SET_COMMUNITY_STATS', payload: communityStats })
        }
      })
      .catch(() => {
        if (!cancelled) {
          dispatch({ type: 'COMMUNITY_STATS_UNAVAILABLE' })
        }
      })

    return () => {
      cancelled = true
    }
  }, [
    gameMode,
    state.isInitialized,
    state.lower.length,
    state.puzzle_number,
    state.upper.length,
    state.won,
  ])

  // Update the golden indices when the upper or lower words change
  // clear the golden indices when the game is won
  useEffect(() => {
    if (!state.isInitialized) {
      return
    }
    const lastUpperWord = state.upper[state.upper.length - 1][0]
    const lastLowerWord = state.lower[state.lower.length - 1][0]

    if (state.won) {
      setGoldenIndices([])
    } else {
      const matches = getMatchingLetters(lastUpperWord, lastLowerWord)
      setGoldenIndices(matches)
    }
  }, [state.upper, state.lower, state.won])

  useEffect(() => {
    if (!state.isInitialized) {
      return
    }
    setLastAdded('upper')
  }, [state.upper])

  useEffect(() => {
    if (!state.isInitialized) {
      return
    }
    setLastAdded('lower')
  }, [state.lower])

  // Initializing the game mode
  useEffect(() => {
    if (!statsState.isInitialized || state.isInitialized) {
      return
    }
    try {
      const gameData = Storage.getGameData(gameMode) || {}
      const currentGame = gameData.currentGame
      if (!currentGame) {
        dispatch({
          type: 'INITIALIZE_NEW_GAME',
          payload: {
            gameMode: gameMode.slice(0, -1),
            wordLength: parseInt(gameMode.slice(-1)),
            todaysWords: todaysWordsByLength,
          },
        })
      } else if (currentGame.puzzle_number === state.puzzle_number) {
        dispatch({
          type: 'LOAD_SAVED_STATE',
          payload: { savedGameState: currentGame },
        })
      } else {
        dispatch({
          type: 'HANDLE_STALE_GAME_DATA',
          payload: { currentGame, statsState, statsDispatch },
        })

        dispatch({
          type: 'INITIALIZE_NEW_GAME',
          payload: {
            gameMode: gameMode.slice(0, -1),
            wordLength: parseInt(gameMode.slice(-1)),
            hasSeenRules: currentGame.hasSeenRules,
            todaysWords: todaysWordsByLength,
          },
        })
      }
    } catch (error) {
      console.error('Error loading game data:', error)
    }
    dispatch({ type: 'INIT_STATE_COMPLETED' })
  }, [statsState.isInitialized])

  // Start the game automatically after 30 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      dispatch({ type: 'SET_GAME_STARTED' })
    }, 30000) // 30 seconds

    // Cleanup: clear the timer if the component unmounts before the timeout
    return () => clearTimeout(timer)
  }, []) // Empty dependency array ensures this runs once after initial render

  // Dispatch the loss to gameStats and reset here
  useEffect(() => {
    if (state.handleLoss) {
      statsDispatch({ type: 'LOSS_UP' })
      setHandleLoss(false)
      showWarningSnackbar(<>Woops! You never finished the previous puzzle!</>)
      dispatch({ type: 'RESET_HANDLE_LOSS' })
    }
  }, [state.handleLoss, statsDispatch])

  // Dispatch the streak down to gameStats and reset here
  useEffect(() => {
    if (state.handleStreakDown) {
      statsDispatch({ type: 'STREAK_DOWN' })
      setHandleStreakDown(false)
      showWarningSnackbar(
        <>
          Your winning streak was broken!
          <br /> Make sure to play and finish every day!
        </>,
      )
      dispatch({ type: 'RESET_HANDLE_STREAK_DOWN' }) 
    }
  }, [state.handleStreakDown, statsDispatch])

  // Save the current game state to local storage
  useEffect(() => {
    if (!state.isInitialized) {
      return
    }
    const currentGame = {
      upper: state.upper,
      link: state.link,
      lower: state.lower,
      won: state.won,
      wasStarted: state.wasStarted,
      bestScore: state.bestScore,
      avgScore: state.avgScore,
      difficultyRank: state.difficultyRank,
      difficultyLabel: state.difficultyLabel,
      puzzle_number: state.puzzle_number,
      hasSeenRules: state.hasSeenRules,
    }
    Storage.saveCurrentGame(gameMode, currentGame)
  }, [
    state.upper,
    state.link,
    state.lower,
    state.won,
    state.wasStarted,
    state.bestScore,
    state.avgScore,
    state.difficultyRank,
    state.difficultyLabel,
    state.puzzle_number,
    gameMode,
  ])

  return (
    <GameStateContext.Provider
      value={{
        state,
        dispatch,
        lastKeyPressed,
        setLastKeyPressed,
        goldenIndices: goldenIndices,
        lastAdded: lastAdded,
      }}
    >
      <LinkRowRefContext.Provider value={{ ref: linkRowRef, centerLinkDiv }}>
        {children}
      </LinkRowRefContext.Provider>
    </GameStateContext.Provider>
  )
}
